import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthShell } from "@/components/AuthShell";
import { PasswordInput } from "@/components/PasswordInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, WarningCircle, Buildings } from "@phosphor-icons/react";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [token, setToken] = useState("");
  const [info, setInfo] = useState(null);
  const [invalid, setInvalid] = useState("");
  const [name, setName] = useState("");
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const t = params.get("token") || "";
    setToken(t);
    window.history.replaceState({}, document.title, "/accept-invite");
    if (!t) { setInvalid("This invitation link is missing its token."); return; }
    api.get(`/auth/invite-info?token=${encodeURIComponent(t)}`)
      .then((r) => { setInfo(r.data); setName(r.data.name || ""); })
      .catch((err) => setInvalid(apiError(err.response?.data?.detail)));
  }, []); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) return setError("Password must be at least 8 characters.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/accept-invite", { token, password: pw, name });
      setUser(data);
      navigate("/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (invalid) {
    return (
      <AuthShell title="Invitation" subtitle="">
        <div data-testid="invite-invalid" className="flex gap-3 text-sm text-stone-700 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <WarningCircle size={20} weight="fill" className="text-amber-600 shrink-0" />
          <span>{invalid} <Link to="/login" className="text-[#C2410C] underline">Go to sign in</Link></span>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Accept your invitation"
      subtitle={info ? `Join ${info.org_name || "the workspace"} on Inspiro Interface.` : "Loading your invitation…"}>
      {info && (
        <form onSubmit={submit} className="space-y-4">
          <div className="flex items-center gap-3 bg-stone-50 border border-stone-200 rounded-xl p-3">
            <span className="w-9 h-9 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center">
              <Buildings size={18} weight="duotone" className="text-[#C2410C]" />
            </span>
            <div className="min-w-0">
              <div className="font-medium text-sm text-stone-900 truncate">{info.org_name}</div>
              <div data-testid="invite-email" className="text-xs text-stone-500 truncate">{info.email}</div>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500">Your name</Label>
            <Input data-testid="invite-name" required value={name} onChange={(e) => setName(e.target.value)}
              className="mt-1 rounded-xl" placeholder="Jane Doe" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500">Create a password</Label>
            <PasswordInput testid="invite-password" required value={pw}
              onChange={(e) => setPw(e.target.value)} className="mt-1 rounded-xl" placeholder="At least 8 characters" />
          </div>
          {error && <div data-testid="invite-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 px-3 py-2 rounded-xl">{error}</div>}
          <Button data-testid="invite-submit" type="submit" disabled={loading}
            className="w-full rounded-xl bg-[#C2410C] hover:bg-[#9A3412] transition-colors duration-200 gap-2">
            {loading ? "Joining…" : "Join workspace"} <ArrowRight size={16} />
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
